<script>
import {
  getDate,
  getTime,
  getShortMonth,
  getShortDayOfWeek,
} from "@/modules/helpers/day";

import { monthNames } from "@/modules/constants/month_names";

export default {
  props: {
    poster: {
      type: Object,
      required: true,
    },
  },

  setup() {
    function getTroupe(staging) {
      if (!staging.length) return [];
      const troupe = [];
      staging.forEach((actor) => {
        troupe.push(`${actor?.title}: ${actor?.value?.name}`);
      });
      return troupe;
    }

    return {
      getDate,
      getTime,
      getShortDayOfWeek,
      getShortMonth,
      monthNames,
      getTroupe,
    };
  },
  computed: {
    posterIcon() {
      return this.poster?.img?.fileKey ?? "";
    },
  },
  data() {
    return {
      hover: false,
    };
  },
  methods: {
    hoverOver() {
      this.hover = true;
    },
    hoverLeave() {
      this.hover = false;
    },
  },
};
</script>

<template>
  <article
    class="px-4 xl:px-0 relative bg-white rounded-lg grid grid-cols-12 py-4 xl:py-7 gap-2 gap-y-4 xl:gap-7 xl:gap-y-7"
  >
    <div
      class="col-start-10 col-end-13 xl:col-end-2 xl:col-start-1 xl:pl-8 flex flex-col"
    >
      <div class="xl:hidden mx-auto relative flex justify-center pt-2">
        <span class="h-12 w-12 flex transform rotate-330">
          <div class="h-12 w-12 bg-akcent flex rounded-md">
            <p
              class="m-auto origin-center transform rotate-30 text-white text-lg font-semibold"
            >
              {{ poster.perform.age_limit }}
            </p>
          </div>
        </span>
      </div>
      <div class="text-center xl:ml-auto pt-4 xl:pt-0">
        <p class="ns text-5xl font-semibold leading-none">
          {{ getDate(poster.date) }}
        </p>
        <p class="font-sans text-2.5xl font-semibold leading-none">
          {{ getShortMonth(poster.date) }}
        </p>
        <p class="font-sans text-lg2">{{ getTime(poster.date) }}</p>
        <p class="font-sans text-lg2 text-neutral">
          {{ getShortDayOfWeek(poster.date) }}
        </p>
      </div>
    </div>
    <div class="row-start-1 col-start-1 xl:col-start-2 col-end-10 xl:col-end-6">
      <router-link :to="`/theater/performs/${poster.perform._id}`">
        <img
          loading="lazy"
          class="rounded-lg object-cover h-48 xl:h-71 w-full"
          :src="`https://ucarecdn.com/${poster.perform.img.fileKey}/-/format/auto/-/quality/smart/image.png`"
        />
      </router-link>
    </div>
    <div
      class="row-start-2 row-end-3 xl:row-start-1 xl:row-end-2 col-start-1 col-end-13 xl:col-start-6 xl:col-end-11 flex flex-col space-y-5 justify-between"
    >
      <div class="flex flex-col space-y-5">
        <div class="flex flex-row space-x-7">
          <router-link :to="`/theater/performs/${poster.perform._id}`">
            <h2
              class="font-sans text-xl text-dark-gray leading-none my-auto hover:underline pt-0.5"
            >
              {{ poster.perform.name }}
            </h2>
          </router-link>

          <r-button
            v-if="poster.kind === 'link' || poster.isTouring"
            size="sm"
            defaultColor="dark-gray"
            class="my-auto text-sm"
            cursor="default"
          >
            {{
              poster.tourInfo.length ? poster.tourInfo : "Гастроли"
            }}</r-button
          >

          <r-button
            v-else-if="poster.perform.isPremiere"
            size="sm"
            class="my-auto text-sm"
            cursor="default"
            >Премьера</r-button
          >
        </div>

        <p
          class="font-sans font-regular text-akcent text-lg"
          style="margin-top: 4px !important"
        >
          {{ poster.perform.genre }}
        </p>

        <div>
          <p class="font-sans font-regular text-neutral text-lg">
            Автор:
            <b class="text-lg font-semibold">{{ poster.perform.author }}</b>
          </p>
          <p class="font-sans font-regular text-neutral text-lg pt-1/2">
            Режиссер:
            <b class="text-lg font-semibold">{{ poster.perform.producer }}</b>
          </p>
        </div>

        <div>
          <p class="font-sans font-regular text-neutral text-lg">
            {{ poster.perform.snippet }}
          </p>
        </div>
      </div>
      <div
        class="flex flex-row space-x-7 justify-center xl:justify-start xl:mt-auto"
      >
        <RParticipantsDropdown :options="getTroupe(poster.troupe)">
          Состав
        </RParticipantsDropdown>

        <nuxt-link
          :to="`/buy/${poster.perform._id}/${poster._id}`"
          class="mt-auto"
        >
          <button
            :disabled="!poster.isActive"
            class="text-center text-sm transition bg-primary duration-300 ease-out rounded-md font-semibold leading-none focus:outline-none py-10p px-5 text-white"
            :class="[
              poster.isActive
                ? 'hover:bg-cherry cursor-pointer w-34'
                : 'bg-opacity-20 cursor-default w-38',
            ]"
          >
            {{ poster.isActive ? "Купить билет" : "Билеты проданы" }}
          </button>
        </nuxt-link>
      </div>
    </div>
    <div
      class="col-start-10 xl:col-start-11 col-end-13 hidden xl:flex pr-10 xl:pr-3 -mt-1 xl:mt-9"
    >
      <div class="flex flex-row space-x-1.5 ml-auto origin-top-right h-px">
        <div
          class="h-12 w-12 -mt-1.5 bg-akcent flex rounded-md"
          style="transform: rotate(64deg)"
        >
          <p
            class="m-auto origin-center text-white text-lg font-semibold"
            style="transform: rotate(-64deg)"
          >
            {{ poster.perform.age_limit }}
          </p>
        </div>

        <nuxt-link :to="`/theater/halls/${poster.perform.hall.to}`">
          <div class="flex -mt-11 -ml-3.5">
            <r-icon
              :icon="poster.perform.hall.icon"
              @mouseover="hoverOver"
              @mouseleave="hoverLeave"
              class="w-22 h-22 cursor-pointer placeholder"
            />
          </div>
        </nuxt-link>
      </div>
    </div>
    <div
      class="hidden absolute right-4 top-32 my-auto xl:block"
      v-if="posterIcon != ''"
    >
      <img
        loading="lazy"
        class="object-cover rounded-lg w-32 h-15"
        :src="`https://ucarecdn.com/${poster.img.fileKey}/-/format/auto/-/quality/smart/image.png`"
      />
    </div>
    <div
      class="titleBlock z-50 text-lato text-sm font-normal transform text-center text-neutral bg-white rounded-md"
      v-show="hover"
    >
      {{ poster.perform.hall.name }}
    </div>
  </article>
</template>

<style scoped>
.titleBlock {
  text-shadow: 0px 2px 4px #0000000a;
  padding: 5px 17px;
  box-shadow: 0px 2px 4px #0000000a;
  height: 25px;
  position: absolute;
  top: 37px;
  right: 78px;
}
</style>
